import {
	FiFileText,
	FiUsers,
	FiCalendar,
	FiClock,
	FiRotateCw,
	FiUser,
} from "react-icons/fi";
import {
	contractsService,
	groupsService,
	calendarsService,
	shiftsService,
	shiftPatternsService,
	employeesService,
} from "services";
import { isHaddockEnvironment } from "utils/documentUtils";

/**
 * Interruptor global de la feature de onboarding.
 *
 * Ponlo en `false` para deshabilitar TODO el onboarding sin borrar nada:
 *  - card "Primeros pasos" del sidebar
 *  - checklist de la página /getting-started
 *  - redirección a /getting-started tras el login
 *  - lectura de flags y preview de ítems (no se hacen las peticiones)
 *
 * Para reactivar la feature más adelante, vuelve a ponerlo en `true`.
 */
export const ONBOARDING_ENABLED = true;

/** Ruta de la página dedicada de onboarding. */
export const ONBOARDING_ROUTE = "/getting-started";

/** Máximo de ítems mostrados por card en el checklist. */
export const ONBOARDING_PREVIEW_LIMIT = 5;

const ONBOARDING_PREVIEW_FETCH = {
	page: 0,
	page_size: ONBOARDING_PREVIEW_LIMIT,
	sort: "-id",
};

/**
 * Normaliza la respuesta de los services a una lista.
 * Algunos endpoints devuelven `{ ok, data: [...] }`, otros un array crudo
 * (con `.ok` adjunto por handleResponse) o paginado `{ data, pagination }`.
 */
export const extractList = (res) => {
	if (!res || res.ok === false) return [];
	if (Array.isArray(res)) return res;
	if (Array.isArray(res.data)) return res.data;
	if (Array.isArray(res.content)) return res.content;
	return [];
};

/** Etiqueta visible de un ítem creado en la card del paso. */
export const getStepItemLabel = (stepKey, item) => {
	if (!item) return "";

	switch (stepKey) {
		case "employees":
			return [item.name, item.surname].filter(Boolean).join(" ");
		default:
			return item.name || "";
	}
};

/** Presets rápidos para crear turnos desde el onboarding. */
export const SHIFT_QUICK_CREATE_PRESETS = [
	{ key: "9-14", start: "09:00", end: "14:00" },
	{ key: "8-14", start: "08:00", end: "14:00" },
	{ key: "9-18", start: "09:00", end: "18:00" },
	{ key: "14-20", start: "14:00", end: "20:00" },
];

/**
 * Flags de completado explícito en GET/PUT `/companies/{id}`.
 * El paso de calendarios (`holidays`) usa `onboarding_calendars_complete`.
 */
export const ONBOARDING_STEP_COMPLETE_FIELDS = {
	contracts: "onboarding_contracts_complete",
	groups: "onboarding_groups_complete",
	holidays: "onboarding_calendars_complete",
	shifts: "onboarding_shifts_complete",
	shiftPatterns: "onboarding_shift_patterns_complete",
	employees: "onboarding_employees_complete",
};

export const getStepCompleteField = (stepKey) =>
	ONBOARDING_STEP_COMPLETE_FIELDS[stepKey];

export const isStepMarkedComplete = (company, stepKey) => {
	const field = getStepCompleteField(stepKey);
	return !!field && company?.[field] === true;
};

export const buildStepCompletePatch = (stepKey, complete) => {
	const field = getStepCompleteField(stepKey);
	if (!field) return {};
	return { [field]: !!complete };
};

/**
 * Patch del CRUD de companies para volver a mostrar el onboarding
 * con todos los pasos sin marcar (0/6 en Plain, 0/5 en Haddock).
 */
export const buildOnboardingRestartPatch = () => {
	const patch = { onboarding_complete: false };
	Object.values(ONBOARDING_STEP_COMPLETE_FIELDS).forEach((field) => {
		patch[field] = false;
	});
	return patch;
};

/** Combina preview de ítems con el completado explícito del company. */
export const mergeStepsWithCompanyFlags = (
	stepDefs,
	company,
	previews = []
) => {
	const itemsByKey = previews.reduce((acc, preview) => {
		acc[preview.key] = preview.items || [];
		return acc;
	}, {});

	return stepDefs.map((step) => ({
		key: step.key,
		done: isStepMarkedComplete(company, step.key),
		items: itemsByKey[step.key] || [],
	}));
};

export const countCompletedSteps = (steps = []) =>
	steps.filter((step) => step.done).length;

export const didJustCompleteAll = (wasComplete, isComplete) =>
	!wasComplete && !!isComplete;

/** Estilo opcional del chip (p. ej. color de fondo del turno). */
export const getStepItemStyle = (stepKey, item) => {
	if (stepKey === "shifts" && item?.background_color) {
		return { backgroundColor: item.background_color };
	}
	return undefined;
};

/** Pasos excluidos del onboarding en clientes Haddock. */
export const HADDOCK_EXCLUDED_STEP_KEYS = ["shiftPatterns"];

/**
 * Definición declarativa de los pasos del onboarding.
 *
 * Cada paso es autocontenido: icono, ruta de destino, claves i18n y un
 * `fetchItems()` que devuelve la lista de elementos creados.
 *
 * Añadir/quitar un paso es tan simple como editar este array.
 */
export const ALL_ONBOARDING_STEPS = [
	{
		key: "contracts",
		icon: FiFileText,
		to: "/contracts",
		fetchItems: () => contractsService.getAll({ ...ONBOARDING_PREVIEW_FETCH }),
	},
	{
		key: "groups",
		icon: FiUsers,
		to: "/groups",
		fetchItems: () => groupsService.getAll({ ...ONBOARDING_PREVIEW_FETCH }),
	},
	{
		key: "holidays",
		icon: FiCalendar,
		to: "/calendars",
		fetchItems: () => calendarsService.getAll({ ...ONBOARDING_PREVIEW_FETCH }),
	},
	{
		key: "shifts",
		icon: FiClock,
		to: "/shifts",
		fetchItems: () => shiftsService.getAll({ ...ONBOARDING_PREVIEW_FETCH }),
	},
	{
		key: "shiftPatterns",
		icon: FiRotateCw,
		to: "/shift-patterns",
		fetchItems: () =>
			shiftPatternsService.getAll({ ...ONBOARDING_PREVIEW_FETCH }),
	},
	{
		key: "employees",
		icon: FiUser,
		to: "/employees",
		fetchItems: () =>
			employeesService.getAll({
				...ONBOARDING_PREVIEW_FETCH,
				variant: "select",
			}),
	},
];

/** Pasos activos según el entorno (Haddock omite ruedas de turnos). */
export const getOnboardingSteps = () => {
	if (!isHaddockEnvironment()) {
		return ALL_ONBOARDING_STEPS;
	}

	return ALL_ONBOARDING_STEPS.filter(
		(step) => !HADDOCK_EXCLUDED_STEP_KEYS.includes(step.key)
	);
};

export const getOnboardingTotalSteps = () => getOnboardingSteps().length;

/** @deprecated Usar `getOnboardingSteps()` para respetar exclusiones por entorno. */
export const ONBOARDING_STEPS = ALL_ONBOARDING_STEPS;

/** @deprecated Usar `getOnboardingTotalSteps()`. */
export const ONBOARDING_TOTAL_STEPS = ALL_ONBOARDING_STEPS.length;
